import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Divider} from 'src/design/atoms/Divider';
import {ScrollArea} from 'src/design/atoms/ScrollArea';
import type {FC} from 'react';
import {useRef} from 'react';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {FullScreenModal} from 'src/design/molecules/FullScreenModal';
import {useChatTranscriptExport} from 'src/pages/Modules/Assistant/shared/export/useChatTranscriptExport';
import {useAssistantChat} from 'src/pages/Modules/Assistant/shared/useAssistantChat';
import {useStickToBottom} from 'src/pages/Modules/Assistant/shared/useStickToBottom';
import {ChatComposer} from './ChatComposer';
import {ChatExportMenu} from './ChatExportMenu';
import {CONTINUE_MESSAGE, ChatTranscript} from './ChatTranscript';

export interface ChatPanelProps {
  conversationId: number;
  title?: string;
  /** Hosts the panel in a FullScreenModal (below `sm`) instead of a SectionCard. */
  fullScreen?: boolean;
  onClose?: () => void;
  onOpenMemory?: () => void;
}

export const ChatPanel: FC<ChatPanelProps> = ({
  conversationId,
  title,
  fullScreen = false,
  onClose,
  onOpenMemory,
}) => {
  const {
    messages,
    isMessagesLoading,
    isMessagesError,
    pendingUserContent,
    streamItems,
    activeTool,
    isStreaming,
    streamError,
    streamDone,
    sendMessage,
    stop,
  } = useAssistantChat(conversationId);

  const transcriptExport = useChatTranscriptExport({
    title,
    messages,
    isStreaming,
  });

  const viewportRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = useStickToBottom(viewportRef, [
    messages,
    streamItems,
    pendingUserContent,
    streamError,
  ]);

  const handleSend = (content: string, options?: {thinkHarder: boolean}) => {
    scrollToBottom();
    void sendMessage(content, options);
  };

  const exportMenu = <ChatExportMenu {...transcriptExport} />;

  const body = (
    <Box
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}>
      {isMessagesError ? (
        <Box p="md">
          <Alert color="red" title="Unable to load conversation">
            This conversation could not be loaded. Please try again.
          </Alert>
        </Box>
      ) : (
        <>
          <ScrollArea
            viewportRef={viewportRef}
            style={{flex: 1, minHeight: 0}}
            styles={{viewport: {height: '100%'}}}>
            <ChatTranscript
              conversationId={conversationId}
              messages={messages}
              isLoading={isMessagesLoading}
              pendingUserContent={pendingUserContent}
              streamItems={streamItems}
              activeTool={activeTool}
              isStreaming={isStreaming}
              streamError={streamError}
              streamDone={streamDone}
              onSuggestion={content => handleSend(content)}
              onContinue={() => handleSend(CONTINUE_MESSAGE)}
              onOpenMemory={onOpenMemory}
            />
          </ScrollArea>
          <Divider />
          <ChatComposer
            isStreaming={isStreaming}
            disabled={isMessagesLoading}
            onSend={handleSend}
            onStop={stop}
          />
        </>
      )}
    </Box>
  );

  if (fullScreen) {
    return (
      <FullScreenModal
        open
        onCloseCallback={() => onClose?.()}
        title={title || 'Scout'}
        headerRightSection={exportMenu}
        fillHeight>
        {body}
      </FullScreenModal>
    );
  }

  return (
    <SectionCard
      title={title || 'Scout'}
      rightSection={exportMenu}
      h="100%"
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minWidth: 0,
        width: '100%',
      }}>
      {body}
    </SectionCard>
  );
};
